import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  LuBell,
  LuRefreshCw,
  LuSend,
  LuCircleCheck,
  LuCircleX,
  LuCircleMinus,
  LuExternalLink,
} from "react-icons/lu";
import { api, ApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonCard, SkeletonList } from "@/components/ui/skeleton";

const STATUS_META: Record<
  string,
  { label: string; cls: string; icon: typeof LuCircleCheck }
> = {
  sent: {
    label: "Sent",
    cls: "text-[#6ee7b7] bg-[rgba(66,184,131,0.1)] border-[rgba(66,184,131,0.3)]",
    icon: LuCircleCheck,
  },
  failed: {
    label: "Failed",
    cls: "text-[#fca5a5] bg-[rgba(237,66,69,0.1)] border-[rgba(237,66,69,0.3)]",
    icon: LuCircleX,
  },
  skipped: {
    label: "Skipped",
    cls: "text-[var(--color-text-subtle)] bg-[var(--color-elevated)] border-[var(--color-border)]",
    icon: LuCircleMinus,
  },
};

export function NotificationsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<"" | "sent" | "failed" | "skipped">("");
  const [eventFilter, setEventFilter] = useState("");

  const config = useQuery({
    queryKey: ["console", "admin", "notifications", "config"],
    queryFn: () => api.adminNotifications.config(),
  });

  const list = useQuery({
    queryKey: ["console", "admin", "notifications", { statusFilter, eventFilter }],
    queryFn: () =>
      api.adminNotifications.list({
        limit: 100,
        status: statusFilter || undefined,
        event: eventFilter || undefined,
      }),
    refetchInterval: 60_000,
  });

  const testMut = useMutation({
    mutationFn: (channel: "discord-support") => api.adminNotifications.test(channel),
    onSuccess: (res) => {
      if (res.success) {
        toast.success("Test sent. Check the channel.");
      } else if (!res.configured) {
        toast.error("Channel not configured. Set the secret first.");
      } else {
        toast.error(res.error ?? "Test failed. See activity log.");
      }
      qc.invalidateQueries({ queryKey: ["console", "admin", "notifications"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Test request failed."),
  });

  return (
    <>
      <PageHeader
        icon={LuBell}
        title="Notifications"
        description="Where the operator console sends alerts. Configure channels, fire a test ping, and watch every send."
        actions={
          <Button
            variant="secondary"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            <LuRefreshCw className={`w-4 h-4 ${list.isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <div className="p-8 space-y-6">
        {/* Channels */}
        {config.isLoading && <SkeletonCard rows={2} />}
        {config.data && (
          <Card>
            <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center gap-2">
              <LuBell className="w-4 h-4 text-[var(--color-text-subtle)]" />
              <p className="text-sm font-semibold text-[var(--color-text)]">Channels</p>
            </div>
            <div className="divide-y divide-[var(--color-border)]">
              {Object.entries(config.data.channels).map(([id, ch]) => (
                <div
                  key={id}
                  className="px-5 py-4 flex items-center justify-between gap-4"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className={`w-9 h-9 rounded-md flex items-center justify-center border shrink-0 ${
                        ch.configured
                          ? "bg-[rgba(66,184,131,0.08)] border-[rgba(66,184,131,0.3)] text-[#6ee7b7]"
                          : "bg-[var(--color-elevated)] border-[var(--color-border)] text-[var(--color-text-subtle)]"
                      }`}
                    >
                      <LuBell className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-[var(--color-text)] capitalize">
                          {id.replace(/-/g, " ")}
                        </p>
                        <span
                          className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                            ch.configured
                              ? "bg-[rgba(66,184,131,0.1)] border-[rgba(66,184,131,0.3)] text-[#6ee7b7]"
                              : "bg-[var(--color-elevated)] border-[var(--color-border)] text-[var(--color-text-subtle)]"
                          }`}
                        >
                          {ch.configured ? "Configured" : "Not configured"}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-[var(--color-elevated)] border-[var(--color-border)] text-[var(--color-text-muted)]">
                          {ch.channel}
                        </span>
                      </div>
                      <p className="text-[11px] text-[var(--color-text-muted)] mt-1">
                        Fires on <span className="font-mono">{ch.event}</span>
                      </p>
                      {!ch.configured && (
                        <p className="text-[11px] text-[var(--color-text-subtle)] mt-1 font-mono">
                          wrangler secret put {ch.secretName}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={ch.configured ? "primary" : "secondary"}
                    onClick={() => testMut.mutate("discord-support")}
                    disabled={testMut.isPending || !ch.configured}
                    title={ch.configured ? "Send a test ping" : "Configure the secret first"}
                  >
                    <LuSend className="w-3.5 h-3.5" />
                    {testMut.isPending ? "Sending..." : "Send test"}
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Summary */}
        {list.data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Sent (lifetime)" value={list.data.summary.sent} tone="ok" />
            <Stat label="Failed" value={list.data.summary.failed} tone="err" />
            <Stat label="Skipped" value={list.data.summary.skipped} tone="muted" />
            <Stat label="Last 24h" value={list.data.summary.last24h} tone="violet" />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)]"
          >
            <option value="">All statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="skipped">Skipped</option>
          </select>
          <input
            type="text"
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            placeholder="Filter by event (e.g. ticket.created)"
            className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:border-[var(--color-accent)] flex-1 max-w-md"
          />
        </div>

        {/* Activity log */}
        {list.isLoading && <SkeletonList rows={6} />}
        {list.data && list.data.notifications.length === 0 && (
          <Card>
            <div className="px-5 py-12 text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-[var(--color-elevated)] flex items-center justify-center">
                <LuBell className="w-5 h-5 text-[var(--color-text-subtle)]" />
              </div>
              <p className="text-sm text-[var(--color-text-muted)]">
                Nothing sent yet. Notifications appear here as soon as anything fires.
              </p>
            </div>
          </Card>
        )}
        {list.data && list.data.notifications.length > 0 && (
          <Card>
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg)]/40">
                <tr className="text-left text-[var(--color-text-subtle)] border-b border-[var(--color-border)]">
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium">
                    Status
                  </th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium">
                    Event
                  </th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium">
                    Channel
                  </th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium">
                    Target
                  </th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium text-right">
                    Latency
                  </th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium text-right">
                    When
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.notifications.map((n) => {
                  const meta = STATUS_META[n.status] ?? STATUS_META.skipped;
                  const Icon = meta.icon;
                  return (
                    <tr
                      key={n.id}
                      className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-elevated)]/30 transition-colors"
                    >
                      <td className="px-5 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] uppercase tracking-wider border ${meta.cls}`}
                        >
                          <Icon className="w-3 h-3" />
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-[var(--color-text)]">
                        {n.event}
                      </td>
                      <td className="px-5 py-3 text-xs text-[var(--color-text-muted)] capitalize">
                        {n.channel}
                      </td>
                      <td className="px-5 py-3 max-w-[280px]">
                        {n.target ? (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-mono text-[var(--color-text-muted)] truncate"
                            title={n.target}
                          >
                            <LuExternalLink className="w-3 h-3 shrink-0" />
                            {n.target}
                          </span>
                        ) : (
                          <span className="text-[11px] text-[var(--color-text-subtle)]">—</span>
                        )}
                        {n.error && (
                          <p
                            className="text-[10px] text-[#fca5a5] truncate mt-0.5"
                            title={n.error}
                          >
                            {n.error}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right text-xs text-[var(--color-text-muted)] font-mono tabular-nums">
                        {n.durationMs != null ? `${n.durationMs}ms` : "—"}
                      </td>
                      <td className="px-5 py-3 text-right text-xs text-[var(--color-text-subtle)] font-mono tabular-nums whitespace-nowrap">
                        {new Date(n.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {list.data.pagination.total > list.data.notifications.length && (
              <div className="px-5 py-3 border-t border-[var(--color-border)] text-[11px] text-[var(--color-text-subtle)]">
                Showing {list.data.notifications.length} of{" "}
                {list.data.pagination.total.toLocaleString()}.
              </div>
            )}
          </Card>
        )}
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "ok" | "err" | "muted" | "violet";
}) {
  const text =
    tone === "ok"
      ? "text-[#6ee7b7]"
      : tone === "err"
        ? "text-[#fca5a5]"
        : tone === "muted"
          ? "text-[var(--color-text-muted)]"
          : "text-[var(--color-accent-light)]";
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
        {label}
      </p>
      <p className={`text-xl font-semibold tabular-nums mt-1 ${text}`}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}
